import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  useCreditDashboard,
  usePortfolioEvolution,
  useProductDistribution,
  useCollectionMetrics,
} from '../../../hooks/useCredit';
import type { DashboardFilters } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatPercent = (value: number) => {
  return `${value.toFixed(2)}%`;
};

export function CreditDashboard() {
  const [filters, setFilters] = useState<DashboardFilters>({ period: '30d' });

  const { data: dashboard, isLoading } = useCreditDashboard(filters);
  const { data: evolution } = usePortfolioEvolution(filters);
  const { data: distribution } = useProductDistribution();
  const { data: collectionMetrics } = useCollectionMetrics();

  const periodOptions = [
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: '90d', label: 'Últimos 90 dias' },
    { value: '1y', label: 'Último ano' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Dashboard de Crédito</h1>
        <Select
          value={filters.period || '30d'}
          onChange={(e) =>
            setFilters({ ...filters, period: e.target.value as DashboardFilters['period'] })
          }
          options={periodOptions}
          className="w-40"
        />
      </div>

      {/* Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Carteira Ativa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(dashboard?.totalPortfolio || 0)}
            </div>
            <p className="text-xs text-gray-500">
              {dashboard?.activeLoansCount || 0} empréstimos ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Desembolsos no Período
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(dashboard?.disbursementVolume || 0)}
            </div>
            <p className="text-xs text-gray-500">
              Ticket médio: {formatCurrency(dashboard?.averageTicket || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Taxa de Inadimplência
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatPercent(dashboard?.defaultRate || 0)}
            </div>
            <p className="text-xs text-gray-500">
              Em atraso: {formatCurrency(dashboard?.totalOverdue || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Taxa de Aprovação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatPercent(dashboard?.approvalRate || 0)}
            </div>
            <p className="text-xs text-gray-500">
              {dashboard?.pendingRequestsCount || 0} solicitações pendentes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Segunda linha de métricas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Prazo Médio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboard?.averageTerm?.toFixed(1) || 0} meses
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Eficiência de Cobrança
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercent(dashboard?.collectionEfficiency || 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Solicitações Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboard?.pendingRequestsCount || 0}
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() => window.location.href = '/admin/credit/requests'}
            >
              Ver Solicitações
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Distribuição por Produto */}
      {distribution && distribution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Produto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {distribution.map((item: any) => (
                <div key={item.productType} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="primary">
                      {item.productType.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {item.loansCount} empréstimos
                    </span>
                  </div>
                  <span className="font-medium">
                    {formatCurrency(item.totalBalance)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Métricas de Cobrança */}
      {collectionMetrics && (
        <Card>
          <CardHeader>
            <CardTitle>Métricas de Cobrança</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Ações Executadas Hoje</p>
                <p className="text-xl font-bold">
                  {collectionMetrics.actionsExecutedToday}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Renegociações Pendentes</p>
                <p className="text-xl font-bold">
                  {collectionMetrics.pendingRenegotiations}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Parcelas por Status</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {collectionMetrics.installmentsByStatus?.map((s: any) => (
                    <Badge key={s.status} variant={s.status === 'OVERDUE' ? 'error' : 'default'}>
                      {s.status}: {s.count}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evolução da Carteira */}
      {evolution && evolution.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Evolução da Carteira</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Mês</th>
                    <th className="text-right py-2">Desembolsado</th>
                    <th className="text-right py-2">Empréstimos</th>
                  </tr>
                </thead>
                <tbody>
                  {evolution.map((item: any) => (
                    <tr key={item.month} className="border-b">
                      <td className="py-2">{item.month}</td>
                      <td className="text-right py-2">
                        {formatCurrency(item.disbursedAmount)}
                      </td>
                      <td className="text-right py-2">{item.loansCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default CreditDashboard;
